import { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import useAppState from '../../../common/state/appState';
import UftCarTilesCarousel from '../../../components/carousels/car-tiles/CarTilesCarousel/UftCarTilesCarousel';
import { KmxContainer } from '../../../components/KmxContainer';
import { IVehicleTile } from '../../../types/vehicleTile';
import { trackEDDL, ANALYTICS_CONSTANTS } from '../../../utilities/analytics';
import HomePageExperience from '../hero/store/HomePageExperience';
import { useUftVehicleDisplay } from '../utilities/uftVehicleDisplay';

interface OpenStoreSelectionModalDetail {
    zipCode: string;
}

const openStoreSelectionModal = ({ zipCode }: OpenStoreSelectionModalDetail) => {
    const detail = { stateOrZip: zipCode };

    trackEDDL({
        event: 'storeChange',
        linkDetails: {
            name: zipCode || '',
            position: 'Shop cars near you',
        },
    });

    // Open modal
    const event = new CustomEvent('kmxOpenStoreSelectionModal', { detail });
    document.documentElement.dispatchEvent(event);
};

interface CarRecommendationsHeaderProps {
    displayText?: string;
}

const CarRecommendationsHeader = ({ displayText }: CarRecommendationsHeaderProps) => {
    const experience = useAppState((state) => state.experience);
    const zipCode = useAppState((state) => state.zipCode);
    if (experience === HomePageExperience.FirstTimeVisitor && zipCode) {
        return (
            <>
                {`${displayText} `}
                <Link color="inherit" component="button" onClick={() => openStoreSelectionModal({ zipCode })}>
                    {zipCode}
                </Link>
            </>
        );
    }

    return <>{displayText}</>;
};

interface CarRecommendationsProps {
    carouselId: string;
}

const CarRecommendations = ({ carouselId }: CarRecommendationsProps) => {
    const uftRecommendedVehiclesLoading = useAppState((state) => state.uftRecommendedVehiclesLoading);
    const uftRecommendedVehiclesLoaded = useAppState((state) => state.uftRecommendedVehiclesLoaded);
    const uftRecommendedVehicles = useAppState((state) => state.uftRecommendedVehicles);
    const searchToken = useAppState((state) => state.searchToken);
    const displayText = useAppState((state) => state.displayText);
    const getUftRecommendedVehicles = useAppState((state) => state.getUftRecommendedVehicles);
    const experience = useAppState((state) => state.experience);
    const isHomebaseLoading = useAppState((state) => state.isLoading);
    const { remainingRecommendedVehicles } = useUftVehicleDisplay();

    const fireImpressionTagOnlyWhenRendered = useCallback(() => {
        const allStockNumbers = remainingRecommendedVehicles
            .map((vehicle: IVehicleTile) => vehicle.stockNumber)
            .join('|');

        trackEDDL({
            event: ANALYTICS_CONSTANTS.EVENTS.IMPRESSION.RECOMMENDED_CAR,
            recommendations: {
                stockNumber: '',
                similarVehicles: allStockNumbers,
                carouselLength: String(remainingRecommendedVehicles.length),
            },
            module: {
                moduleExperience: `VR|Homepage|${displayText}`,
            },
        });
    }, [remainingRecommendedVehicles, displayText]);

    useEffect(() => {
        if (isHomebaseLoading) {
            // Wait for homebase to load so the experience is properly set before loading
            return;
        }
        if (!uftRecommendedVehiclesLoaded && uftRecommendedVehicles.length === 0) {
            getUftRecommendedVehicles();
        }
        if (uftRecommendedVehiclesLoaded && remainingRecommendedVehicles.length > 0) {
            fireImpressionTagOnlyWhenRendered();
        }
    }, [
        getUftRecommendedVehicles,
        uftRecommendedVehiclesLoaded,
        uftRecommendedVehicles.length,
        remainingRecommendedVehicles.length,
        isHomebaseLoading,
        fireImpressionTagOnlyWhenRendered,
    ]);

    if (uftRecommendedVehiclesLoading || remainingRecommendedVehicles.length === 0) {
        return <></>;
    }

    return (
        <KmxContainer
            maxWidth="xl"
            sx={{
                pt: { xs: 5, sm: 6 },
            }}
        >
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
                <Box>
                    <UftCarTilesCarousel
                        showButton
                        buttonHref={`/cars?searchToken=${searchToken}`}
                        searchToken={searchToken}
                        title={<CarRecommendationsHeader displayText={displayText} />}
                        carouselId={carouselId}
                        vehiclesData={remainingRecommendedVehicles}
                        sectionName="car-recommendations"
                        taggingPosition={displayText ?? 'car recommendations'}
                        nextFocus={
                            experience === HomePageExperience.FirstTimeVisitor
                                ? 'budget-calculator'
                                : 'recommended-categories-section'
                        }
                    />
                </Box>
            </Stack>
        </KmxContainer>
    );
};

export default CarRecommendations;
